import {
  createStyles,
  Paper,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import clsx from "clsx";
import React, { type ReactElement, useCallback, useMemo } from "react";
import arrayUtils from "shared/arrayUtils";
import parserUtils from "shared/parserUtils";
import { useTableSort } from "../../hooks/useTableSort";
import { Show } from "../Show";
import type { CustomTableColumn, PaginationProps } from "./customTable.types";
import CustomTableCell from "./customTableCell";
import CustomTableHeaderCell from "./customTableHeaderCell";
import { useCustomTablePagination } from "./useCustomTablePagination";

type OwnProps<
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
> = {
  className?:
    | string
    | {
        paper?: string;
        root?: string;
        table?: string;
        header?: string;
        headerRow?: string;
        headerCell?: string;
        body?: string;
        bodyRow?: string;
        bodyCell?: string;
      };
  columns: TColumn[];
  getRowId: (row: TRow) => string;
  id: string;
  initialOrder?: {
    orderBy: TCols[keyof TCols];
    order: "asc" | "desc";
  };
  pagination?: PaginationProps;
  renderEmptyTable?: () => ReactElement;
  rows: TRow[];
};

type CustomTableImplProps<
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
> = OwnProps<TRow, TColumn, TCols> & WithStyles<typeof styles>;

function CustomTableImpl<
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
>({
  className,
  classes,
  columns,
  getRowId,
  id,
  initialOrder,
  pagination,
  renderEmptyTable: EmptyTableComponent,
  rows,
  ...rest
}: CustomTableImplProps<TRow, TColumn, TCols>) {
  const { t } = useTranslations();

  const {
    currentPage,
    currentRowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    hasPagination,
    rowsCount,
    rowsPerPageOptions,
  } = useCustomTablePagination(pagination);

  const visibleColumns = useMemo(
    () => columns.filter((col) => !col.hide),
    [columns]
  );

  const columnMap = useMemo(
    () => new Map(columns.map((col) => [col.id, col])),
    [columns]
  );

  const firstSortableColumn = visibleColumns.find((col) => col.isSortable);
  const isTableSortable = firstSortableColumn != null;

  const { handleRequestSort, tableSort } = useTableSort({
    initialOrderBy:
      initialOrder?.orderBy ?? (firstSortableColumn?.id as TCols[keyof TCols]),
    initialOrder: initialOrder?.order ?? "asc",
  });

  const getValueByKey = useCallback(
    (row: TRow, key: TCols[keyof TCols]) => {
      const col = columnMap.get(key);
      if (!col) return null;
      return col?.getRowValue?.(row) ?? null;
    },
    [columnMap]
  );

  const sortedRows = useMemo(() => {
    if (!isTableSortable) {
      return rows;
    }

    return arrayUtils.stableSort({
      getValueByKey: getValueByKey,
      items: rows,
      order: tableSort.order,
      orderBy: tableSort.orderBy,
    });
  }, [
    getValueByKey,
    isTableSortable,
    rows,
    tableSort.order,
    tableSort.orderBy,
  ]);

  const isTableEmpty = sortedRows.length === 0;

  const paginatedRows = useMemo(() => {
    if (pagination == null) {
      return sortedRows;
    }

    return arrayUtils.paginateItems({
      currentPage,
      items: sortedRows,
      rowsPerPage: currentRowsPerPage,
    });
  }, [currentPage, currentRowsPerPage, pagination, sortedRows]);

  if (isTableEmpty && EmptyTableComponent != null) {
    return <EmptyTableComponent />;
  }

  const incomingClasses = parserUtils.parseClassName(className, "root");

  const paperClasses = clsx(classes.paper, incomingClasses.paper);
  const rootClasses = clsx(classes.root, incomingClasses.root);
  const tableClasses = clsx(classes.table, incomingClasses.table);
  const headerClasses = clsx(classes.header, incomingClasses.header);

  return (
    <Paper className={paperClasses} square={true}>
      <div className={rootClasses}>
        <Table className={tableClasses} data-testid={id} id={id} {...rest}>
          <TableHead className={headerClasses}>
            <TableRow className={incomingClasses.headerRow}>
              {visibleColumns.map((column) => (
                <CustomTableHeaderCell
                  align={column.align}
                  className={incomingClasses.headerCell}
                  id={column.id}
                  isActionColumn={column.isActionColumn}
                  isSortable={column.isSortable}
                  key={column.id}
                  leadingElement={column.leadingHeaderElement}
                  onSortRequest={handleRequestSort}
                  order={tableSort.order}
                  orderBy={tableSort.orderBy}
                  styles={column.styles}
                  trailingElement={column.trailingHeaderElement}
                >
                  {column.label}
                </CustomTableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className={incomingClasses.body}>
            {paginatedRows.map((row) => (
              <TableRow key={getRowId(row)} className={incomingClasses.bodyRow}>
                {visibleColumns.map((column) => (
                  <CustomTableCell
                    align={
                      column.align ??
                      (column.isActionColumn ? "center" : "left")
                    }
                    className={incomingClasses.bodyCell}
                    column={column}
                    key={`${column.id}-${getRowId(row)}`}
                    row={row}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Show when={hasPagination}>
        <TablePagination
          component="div"
          count={rowsCount ?? rows.length}
          data-testid={`${id}-pagination`}
          labelDisplayedRows={({ from, to, count }) =>
            t("common:pagination.displayed-rows", { from, to, count })
          }
          labelRowsPerPage={t("common:pagination.rows-per-page")}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          page={currentPage}
          rowsPerPage={currentRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </Show>
    </Paper>
  );
}

const styles = () =>
  createStyles({
    paper: {
      width: "100%",
      maxHeight: 600,
      overflow: "auto",
      marginTop: "20px",
      borderRadius: "16px",
    },
    root: {
      fontFamily: "Roboto",
      maxHeight: 540,
      overflow: "auto",
      width: "100%",
      "&::-webkit-scrollbar": {
        width: 9,
        height: 10,
        borderRadius: 10,
      },
    },
    table: {
      backgroundColor: "white",
      maxHeight: "280px",
      width: "100%",
      "&::-webkit-scrollbar": {
        width: 9,
        height: 10,
        borderRadius: 10,
      },
    },
    header: {
      backgroundColor: "#e1e1e1",
      height: 56,
    },
  });

/**
 * A customizable table component with support for sorting, column visibility, and custom cell rendering.
 * It provides a default implementation for rendering default table headers and table cells.
 * It also have a built-in support for sorting rows based on sortable columns
 * If wanted, the provided rows should already be filtered outside of this component.
 */
const CustomTable = withStyles(styles)(CustomTableImpl) as <
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
>(
  props: OwnProps<TRow, TColumn, TCols>
) => JSX.Element;

export default CustomTable;
